import React, { useEffect, useState } from 'react';
import './input.css';
import { ErrorMessage } from 'formik';
import PasswordTextField from '../password-input';
import { CircularProgress, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Trans, useTranslation } from 'react-i18next';
import { UpperCaseWord } from 'assets/css/common-styles';
import { useSelector } from 'react-redux';
import { InputTooltip } from 'components/input-tooltip/input-tooltip';

const CustomInput = ({
  styling,
  placeholder,
  touched,
  error,
  name,
  value,
  onChange,
  className,
  isFormIk = true,
  handleClickShowPassword,
  PasswordInput,
  inputProps = {},
  loading = false,
  EndAdornment = () => {},
  onEnter,
  validateBorderColor,
  validateBorderCondition,
  validateLoading,
  dataTooltip,
  tooltipStyles,
  onBlur,
  onFocus,
  largerFont,
  borderWidth,
  borderRadius,
  borderBottomWidth,
  isValidate = true,
  errorStyles = {},
  placeholderClass = false,
  disabled = false,
  defaultDropdown = 'on',
  disabledField = false,
  autocomplete,
  headerInputClass,
  focusName = undefined,
  ...rest
}) => {
  // Here validate border condition is added because of user management page where we are validating the user exist in the database or not while adding the subuser.
  const { t } = useTranslation();
  const userAccount = useSelector((state) => state.user.account);
  const useStyles = makeStyles({
    root: {
      ...(error && touched
        ? {
            border: `${borderWidth || '2px'} solid #d8d8d8`,
            borderRadius: borderRadius || '8px',
            borderBottom: `${borderBottomWidth || '2.9px'} solid #d80000`,
            '&.Mui-focused': {
              border: '2px solid #d80000',
            },
          }
        : {
            border: `${borderWidth || '2px'} solid #d8d8d8`,
            borderRadius: borderRadius || '8px',
            borderBottom:
              validateBorderColor && value && !validateLoading
                ? !validateBorderCondition
                  ? `${borderBottomWidth || '2.9px'} solid green`
                  : `${borderBottomWidth || '2.9px'} solid #d80000`
                : `${borderBottomWidth || '2.9px'} solid orange`,
            '&.Mui-focused': {
              border:
                validateBorderColor && value && !validateLoading
                  ? !validateBorderCondition
                    ? `${borderWidth || '2px'} solid green`
                    : `${borderWidth || '2px'} solid #d80000`
                  : ``,
            },
          }),
      '&.MuiFilledInput-root': {
        fontSize: largerFont ? 22 : 15,
      },
      ...styling,
    },
    underline: {
      '&&&:before': {
        borderBottom: 'none',
      },
      '&&:after': {
        borderBottom: 'none',
      },
    },
  });
  const onKeyEnter = (e) => {
    if (e.key === 'Enter') {
      if (onEnter) onEnter(e);
    }
  };
  const [show, setShow] = useState(false);

  const showTooltip = (bool) => {
    setShow(bool);
  };

  useEffect(() => {
    if (focusName) document.getElementById(focusName).focus();
  }, [focusName]);

  const classes = useStyles();
  return (
    <>
      <div
        style={{ display: 'grid', marginBottom: 10, ...styling }}
        className={disabledField ? '' : `${'custom_input ' + className}`}
      >
        {isValidate && (
          <div
            style={{
              height: '1.8rem',
              maxWidth: styling?.width,
              whiteSpace: 'nowrap',
              ...errorStyles,
            }}
          >
            {isFormIk ? (
              <ErrorMessage
                name={name}
                render={(msg) => (
                  <span className="error_messageSty">
                    <Trans
                      i18nKey={msg}
                      values={{
                        currency: userAccount.account_currency,
                        currency_symbol: userAccount.account_currency_symbol,
                      }}
                      components={{ uppercase: <UpperCaseWord /> }}
                    />
                  </span>
                )}
              />
            ) : (
              <>
                {error && (
                  <span className="error_messageSty">
                    <Trans
                      i18nKey={error || 'Wrong format'}
                      values={{
                        currency: userAccount.account_currency,
                        currency_symbol: userAccount.account_currency_symbol,
                      }}
                      components={{ uppercase: <UpperCaseWord /> }}
                    />
                  </span>
                )}
              </>
            )}
          </div>
        )}
        <div
          style={{ position: 'relative' }}
          className={`custom-text-field ${placeholder && 'no_placeholder'} ${
            headerInputClass && 'header_input_class'
          } ${placeholderClass && 'placeholder_class'}`}
        >
          {PasswordInput ? (
            <PasswordTextField
              disabled={disabledField}
              classes={classes}
              handleClickShowPassword={handleClickShowPassword}
              {...rest}
              onKeyPress={onKeyEnter}
              label={placeholder}
              name={name}
              value={value}
              onChange={onChange}
              onFocus={() => {
                showTooltip(true);
                if (onFocus) {
                  onFocus();
                }
              }}
              onBlur={(e) => {
                if (onBlur) onBlur(e);
                showTooltip(false);
              }}
            />
          ) : (
            <TextField
              autoComplete={defaultDropdown}
              disabled={disabled || disabledField}
              InputProps={{
                classes,
              }}
              inputProps={{
                ...inputProps,
                autocomplete: autocomplete,
              }}
              label={placeholder}
              variant="filled"
              id={focusName}
              name={name}
              onKeyPress={onKeyEnter}
              {...rest}
              value={value}
              onChange={onChange}
              onFocus={() => {
                showTooltip(true);
                if (onFocus) {
                  onFocus();
                }
              }}
              onBlur={(e) => {
                if (onBlur) onBlur(e);
                showTooltip(false);
              }}
            />
          )}
          {loading ? (
            <CircularProgress size={20} classes={{ root: 'loading' }} />
          ) : (
            <div className={`end-adornment ${error && 'opacity-3 '}`}>{EndAdornment()}</div>
          )}
          {show && <InputTooltip string12={t('string12')} tooltipStyles={tooltipStyles} dataTooltip={dataTooltip} />}
        </div>
      </div>
    </>
  );
};

export default CustomInput;
